import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { renderAsync } from "docx-preview";
import { Search, X, ChevronUp, ChevronDown } from "lucide-react";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdfjs/pdf.worker.min.js`;

interface ResumeViewerProps {
  url: string;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onLoaded?: () => void;
}

export interface ResumeViewerHandle {
  openSearch: () => void;
}

export interface ResumeZoomControlsProps {
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onSearchClick?: () => void;
  onDownloadClick?: () => void;
}

type ViewerStatus = "loading" | "pdf" | "docx" | "image" | "error";

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;
const DEFAULT_ZOOM = 1.5;

const PDF_EXT = /\.pdf(\?|#|$)/i;
const DOCX_EXT = /\.(docx|doc)(\?|#|$)/i;
const IMG_EXT = /\.(jpe?g|png|gif|webp|svg|bmp)(\?|#|$)/i;

const detectKind = (url: string, blob: Blob): "pdf" | "docx" | "image" | "unknown" => {
  if (PDF_EXT.test(url)) return "pdf";
  if (DOCX_EXT.test(url)) return "docx";
  if (IMG_EXT.test(url)) return "image";
  if (blob.type === "application/pdf" || blob.type.includes("pdf")) return "pdf";
  if (blob.type.includes("wordprocessingml") || blob.type.includes("msword")) return "docx";
  if (blob.type.startsWith("image/")) return "image";
  return "unknown";
};

const zoomButtonStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 6,
  border: "1px solid #e2e8f0",
  background: "#fff",
  color: "#334155",
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

export const ResumeZoomControls: React.FC<ResumeZoomControlsProps> = ({ zoom, onZoomChange, onSearchClick, onDownloadClick }) => {
  const zoomIn = () => onZoomChange(Math.min(MAX_ZOOM, Math.round((zoom + ZOOM_STEP) * 100) / 100));
  const zoomOut = () => onZoomChange(Math.max(MIN_ZOOM, Math.round((zoom - ZOOM_STEP) * 100) / 100));
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {onDownloadClick && (
        <>
          <button onClick={onDownloadClick} title="Download resume" style={{ ...zoomButtonStyle, opacity: 1, userSelect: "none" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
          </button>
          <div style={{ width: 1, height: 16, background: "#cbd5e1", margin: "0 2px" }} />
        </>
      )}
      {onSearchClick && (
        <>
          <button onClick={onSearchClick} title="Search in resume" style={{ ...zoomButtonStyle, opacity: 1, userSelect: "none" }}>
            <Search size={14} />
          </button>
          <div style={{ width: 1, height: 16, background: "#cbd5e1", margin: "0 2px" }} />
        </>
      )}
      <button onClick={zoomOut} disabled={zoom <= MIN_ZOOM} title="Zoom out" style={{ ...zoomButtonStyle, opacity: zoom <= MIN_ZOOM ? 0.4 : 1, userSelect: "none" }}>
        −
      </button>
      <span style={{ minWidth: 46, textAlign: "center", fontSize: "12px", fontWeight: 600, color: "#334155" }}>{Math.round(zoom * 100)}%</span>
      <button onClick={zoomIn} disabled={zoom >= MAX_ZOOM} title="Zoom in" style={{ ...zoomButtonStyle, opacity: zoom >= MAX_ZOOM ? 0.4 : 1, userSelect: "none" }}>
        +
      </button>
    </div>
  );
};

const ResumeViewer = React.forwardRef<ResumeViewerHandle, ResumeViewerProps>(({ url, zoom, onZoomChange, onLoaded }, ref) => {
  const pdfContainerRef = useRef<HTMLDivElement | null>(null);
  const docxContainerRef = useRef<HTMLDivElement | null>(null);
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);
  const renderTokenRef = useRef(0);
  const lastRenderedZoomRef = useRef(1);
  const searchTimerRef = useRef<number | null>(null);
  const [status, setStatus] = useState<ViewerStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCount, setSearchCount] = useState(0);
  const [searchIndex, setSearchIndex] = useState(-1);
  const marksRef = useRef<HTMLElement[]>([]);

  useImperativeHandle(ref, () => ({
    openSearch: () => setSearchOpen(true),
  }));

  const renderPdfPages = async (doc: pdfjsLib.PDFDocumentProxy, container: HTMLElement, zoomScale: number) => {
    const token = ++renderTokenRef.current;
    const containerWidth = container.clientWidth || 900;
    container.innerHTML = "";
    for (let i = 1; i <= doc.numPages; i += 1) {
      if (renderTokenRef.current !== token) return;
      const page = await doc.getPage(i);
      const baseViewport = page.getViewport({ scale: 1 });
      const fitScale = Math.min(1, containerWidth / baseViewport.width);
      const viewport = page.getViewport({ scale: fitScale * zoomScale });

      const pageWrapper = document.createElement("div");
      pageWrapper.style.position = "relative";
      pageWrapper.style.margin = "0 auto 12px";
      pageWrapper.style.width = `${viewport.width}px`;
      pageWrapper.style.height = `${viewport.height}px`;
      pageWrapper.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
      pageWrapper.style.borderRadius = "4px";
      pageWrapper.style.overflow = "hidden";
      pageWrapper.style.backgroundColor = "white";

      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${canvas.width}px`;
      canvas.style.height = `${canvas.height}px`;
      canvas.style.display = "block";
      pageWrapper.appendChild(canvas);

      const textLayerDiv = document.createElement("div");
      textLayerDiv.className = "pdf-text-layer";
      pageWrapper.appendChild(textLayerDiv);

      container.appendChild(pageWrapper);

      await page.render({ canvasContext: canvas.getContext("2d") as CanvasRenderingContext2D, viewport }).promise;

      const textContent = await page.getTextContent();
      await pdfjsLib.renderTextLayer({
        textContentSource: textContent,
        container: textLayerDiv,
        viewport: viewport,
        textDivs: []
      }).promise;

      page.cleanup();
    }
    lastRenderedZoomRef.current = zoomScale;

    // Re-run search if active
    if (searchQuery.trim()) {
      runSearch(searchQuery);
    }
  };

  const clearMarks = () => {
    marksRef.current.forEach((mark) => {
      const parent = mark.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(mark.textContent || ""), mark);
        parent.normalize();
      }
    });
    marksRef.current = [];
  };

  const runSearch = (q: string) => {
    const container = status === "pdf" ? pdfContainerRef.current : docxContainerRef.current;
    clearMarks();
    const needle = q.trim().toLowerCase();
    if (!needle || !container) {
      setSearchCount(0);
      setSearchIndex(-1);
      return;
    }

    const marks: HTMLElement[] = [];
    const textNodes: Text[] = [];
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) textNodes.push(walker.currentNode as Text);

    for (const node of textNodes) {
      let cur: Text | null = node;
      while (cur) {
        const lower = cur.data.toLowerCase();
        const idx = lower.indexOf(needle);
        if (idx === -1) break;
        const end = idx + needle.length;
        const after = cur.splitText(end);
        const mid = cur.splitText(idx);
        const mark = document.createElement("mark");
        mark.style.background = status === "pdf" ? "rgba(253, 224, 71, 0.4)" : "#fde047";
        mark.style.color = status === "pdf" ? "transparent" : "#111";
        mark.style.borderRadius = "2px";
        mark.style.padding = "0 1px";
        const parent = mid.parentNode;
        if (parent) parent.replaceChild(mark, mid);
        mark.appendChild(mid);
        marks.push(mark);
        cur = after;
      }
    }
    marksRef.current = marks;
    setSearchCount(marks.length);
    setSearchIndex(marks.length ? 0 : -1);

    if (marks.length) {
      marks[0].style.background = status === "pdf" ? "rgba(249, 115, 22, 0.5)" : "#f97316";
      marks[0].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const goToMatch = (idx: number) => {
    marksRef.current.forEach((mark, i) => {
      if (i === idx) {
        mark.style.background = status === "pdf" ? "rgba(249, 115, 22, 0.5)" : "#f97316";
        mark.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        mark.style.background = status === "pdf" ? "rgba(253, 224, 71, 0.4)" : "#fde047";
      }
    });
  };

  const clearAllHighlights = () => {
    clearMarks();
    setSearchCount(0);
    setSearchIndex(-1);
  };

  const nextMatch = () => {
    if (!searchCount) return;
    const t = (searchIndex + 1) % searchCount;
    setSearchIndex(t);
    goToMatch(t);
  };

  const prevMatch = () => {
    if (!searchCount) return;
    const t = (searchIndex - 1 + searchCount) % searchCount;
    setSearchIndex(t);
    goToMatch(t);
  };


  const onQueryChange = (q: string) => {
    setSearchQuery(q);
    if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    searchTimerRef.current = window.setTimeout(() => {
      runSearch(q);
    }, 250);
  };

  useEffect(() => {
    let cancelled = false;
    const currentPdfContainer = pdfContainerRef.current;
    const currentDocxContainer = docxContainerRef.current;

    pdfDocRef.current?.destroy();
    pdfDocRef.current = null;
    renderTokenRef.current += 1;
    setStatus("loading");
    if (pdfContainerRef.current) pdfContainerRef.current.innerHTML = "";
    if (docxContainerRef.current) docxContainerRef.current.innerHTML = "";
    marksRef.current = [];
    if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    setSearchOpen(false);
    setSearchQuery("");
    setSearchCount(0);
    setSearchIndex(-1);

    const renderDocx = async (blob: Blob, container: HTMLElement) => {
      await renderAsync(blob, container, undefined, {
        inWrapper: true,
        ignoreWidth: false,
        ignoreHeight: false,
        useBase64URL: true,
      });
    };

    (async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to load resume (${response.status})`);
        const blob = await response.blob();
        const kind = detectKind(url, blob);
        const initialZoom = kind === "image" ? 1.0 : DEFAULT_ZOOM;
        onZoomChange(initialZoom);
        lastRenderedZoomRef.current = initialZoom;

        if (kind === "docx") {
          if (!docxContainerRef.current) throw new Error("Missing docx container");
          await renderDocx(blob, docxContainerRef.current);
        } else if (kind === "pdf") {
          if (!pdfContainerRef.current) throw new Error("Missing pdf container");
          const loadingTask = pdfjsLib.getDocument({ data: await blob.arrayBuffer() });
          const doc = await loadingTask.promise;
          pdfDocRef.current = doc;
          await renderPdfPages(doc, pdfContainerRef.current, initialZoom);
        } else if (kind === "image") {
          setImageUrl(URL.createObjectURL(blob));
        } else {
          try {
            if (!pdfContainerRef.current) throw new Error("Missing pdf container");
            const loadingTask = pdfjsLib.getDocument({ data: await blob.arrayBuffer() });
            const doc = await loadingTask.promise;
            pdfDocRef.current = doc;
            await renderPdfPages(doc, pdfContainerRef.current, DEFAULT_ZOOM);
          } catch {
            if (!docxContainerRef.current) throw new Error("Missing docx container");
            await renderDocx(blob, docxContainerRef.current);
          }
        }

        if (!cancelled) setStatus(kind === "docx" ? "docx" : kind === "image" ? "image" : "pdf");
      } catch (e: any) {
        if (!cancelled) {
          setStatus("error");
          setErrorMessage(e?.message || "Unable to load this resume for preview.");
        }
      } finally {
        if (!cancelled) onLoaded?.();
      }
    })();

    return () => {
      cancelled = true;
      renderTokenRef.current += 1;
      pdfDocRef.current?.destroy();
      pdfDocRef.current = null;
      if (pdfContainerRef.current) pdfContainerRef.current.innerHTML = "";
      if (docxContainerRef.current) docxContainerRef.current.innerHTML = "";
      marksRef.current = [];
      if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    if (status === "pdf" && pdfDocRef.current && pdfContainerRef.current && lastRenderedZoomRef.current !== zoom) {
      renderPdfPages(pdfDocRef.current, pdfContainerRef.current, zoom).catch(() => { });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, status]);

  if (status === "error") {
    return (
      <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "16px", background: "#f8fafc", padding: "32px" }}>
        <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#fee2e2", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", fontWeight: 700 }}>!</div>
        <div style={{ color: "#334155", fontSize: "14px", fontWeight: 600 }}>Could not preview this resume</div>
        <div style={{ color: "#64748b", fontSize: "12px", maxWidth: 420, textAlign: "center" }}>{errorMessage}</div>
        <div style={{ display: "flex", gap: "10px", marginTop: "4px" }}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ padding: "8px 16px", borderRadius: "8px", background: "#0f172a", color: "#fff", fontSize: "13px", fontWeight: 600, textDecoration: "none" }}
          >
            Open in new tab
          </a>
          <a
            href={url}
            download="resume"
            style={{ padding: "8px 16px", borderRadius: "8px", background: "#f1f5f9", color: "#334155", fontSize: "13px", fontWeight: 600, textDecoration: "none", border: "1px solid #e2e8f0" }}
          >
            Download
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", background: "#fff" }}>
      <style>{`
        .pdf-text-layer {
          position: absolute;
          left: 0;
          top: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
          line-height: 1.0;
        }
        .pdf-text-layer > span {
          color: transparent;
          position: absolute;
          white-space: pre;
          cursor: text;
          transform-origin: 0% 0%;
        }
        .pdf-text-layer > span::selection {
          background: rgba(0,0,255,0.2);
        }
      `}</style>
      {status === "loading" && (
        <div style={{ position: "absolute", inset: 0, background: "#f8fafc", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px", zIndex: 2 }}>
          <div style={{ width: 48, height: 48, border: "4px solid #e2e8f0", borderTopColor: "#16a34a", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <span style={{ color: "#64748b", fontSize: "14px", fontWeight: 500 }}>Loading resume...</span>
        </div>
      )}
      <div style={{ display: status === "pdf" ? "block" : "none", width: "100%", height: "100%", overflow: "auto", background: "#e2e8f0" }}>
        <div ref={pdfContainerRef} style={{ padding: "16px", margin: "0 auto" }} />
      </div>

      {status === "image" && imageUrl && (
        <div style={{ width: "100%", height: "100%", overflow: "auto", display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "16px", background: "#e2e8f0" }}>
          <img src={imageUrl} alt="Document Preview" style={{ maxWidth: "100%", transform: `scale(${zoom})`, transformOrigin: "top center", transition: "transform 0.2s", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
        </div>
      )}

      <div style={{ display: status === "docx" ? "block" : "none", width: "100%", height: "100%", overflow: "auto", background: "#e2e8f0" }}>
        <div
          ref={docxContainerRef}
          style={{ padding: "24px", maxWidth: 900, margin: "0 auto", fontFamily: "Calibri, Arial, sans-serif", fontSize: "12px", color: "#111", zoom, transformOrigin: "top center", background: "#fff", minHeight: "100%" }}
        />
      </div>

      {searchOpen && status !== "image" && status !== "loading" && (
        <div style={{ position: "absolute", top: 12, right: 12, zIndex: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "5px 8px", boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }}>
            <Search size={14} color="#64748b" />
            <input
              autoFocus
              value={searchQuery}
              onChange={(e) => onQueryChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (e.shiftKey) prevMatch();
                  else nextMatch();
                } else if (e.key === "Escape") {
                  setSearchOpen(false);
                  setSearchQuery("");
                  clearAllHighlights();
                }
              }}
              placeholder="Search in resume..."
              style={{ border: "none", outline: "none", fontSize: 12, width: 170, color: "#0f172a" }}
            />
            <span style={{ fontSize: 11, color: "#64748b", minWidth: 40, textAlign: "center" }}>
              {searchQuery.trim() ? (searchCount ? `${searchIndex + 1}/${searchCount}` : "0") : ""}
            </span>
            <button onClick={prevMatch} disabled={searchCount === 0} title="Previous match" style={{ ...zoomButtonStyle, width: 24, height: 24, fontSize: 12, opacity: searchCount === 0 ? 0.4 : 1 }}>
              <ChevronUp size={14} />
            </button>
            <button onClick={nextMatch} disabled={searchCount === 0} title="Next match" style={{ ...zoomButtonStyle, width: 24, height: 24, fontSize: 12, opacity: searchCount === 0 ? 0.4 : 1 }}>
              <ChevronDown size={14} />
            </button>
            <button
              onClick={() => {
                setSearchOpen(false);
                setSearchQuery("");
                clearAllHighlights();
              }}
              title="Close search"
              style={{ ...zoomButtonStyle, width: 24, height: 24, fontSize: 12 }}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

ResumeViewer.displayName = "ResumeViewer";

export default ResumeViewer;
